import { useMemo, useState } from 'react'
import type { TransitGraphData } from '@/contexts/EventDataContext'

interface DistanceHeatmapProps {
  data: TransitGraphData
}

function formatDist(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(1)}km`
  return `${Math.round(m)}m`
}

function formatTime(sec: number): string {
  if (sec < 60) return `${Math.round(sec)}s`
  return `${Math.round(sec / 60)}m`
}

interface PairData {
  distance_m: number
  walk_sec: number
  observations: number
}

export function DistanceHeatmap({ data }: DistanceHeatmapProps) {
  const { nodes, edges } = data
  const [sortBy, setSortBy] = useState<'name' | 'traffic'>('traffic')

  const { matrix, sortedNodes, maxDist } = useMemo(() => {
    const rideTypes = new Set(['Rides', 'Adult Ride', 'Kid Ride'])
    const rides = nodes.filter(n => rideTypes.has(n.type))
    const pool = rides.length >= 5 ? rides : nodes
    const sorted = [...pool]
      .sort((a, b) => sortBy === 'traffic' ? b.total_scans - a.total_scans : a.name.localeCompare(b.name))
      .slice(0, 20)

    const idSet = new Set(sorted.map(n => n.id))
    const m = new Map<string, PairData>()
    let max = 0

    for (const e of edges) {
      if (!idSet.has(e.from) || !idSet.has(e.to)) continue
      const pair: PairData = { distance_m: e.distance_m, walk_sec: e.walk_sec, observations: e.observations }
      m.set(`${e.from}-${e.to}`, pair)
      m.set(`${e.to}-${e.from}`, pair)
      if (e.distance_m > max) max = e.distance_m
    }

    return { matrix: m, sortedNodes: sorted, maxDist: max || 1 }
  }, [nodes, edges, sortBy])

  if (sortedNodes.length < 3) {
    return (
      <div className="text-center py-8 text-xs text-ink-soft">
        Not enough attraction data for distance heatmap.
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSortBy('traffic')}
            className={`text-xs px-2.5 py-1 rounded-full transition-colors ${sortBy === 'traffic' ? 'bg-butler-600 text-white' : 'bg-slate-100 text-ink-soft'}`}
          >By Traffic</button>
          <button
            type="button"
            onClick={() => setSortBy('name')}
            className={`text-xs px-2.5 py-1 rounded-full transition-colors ${sortBy === 'name' ? 'bg-butler-600 text-white' : 'bg-slate-100 text-ink-soft'}`}
          >By Name</button>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-ink-soft">
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: 'rgba(0, 112, 171, 0.9)' }} /> Close
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: 'rgba(0, 112, 171, 0.15)' }} /> Far
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="text-[10px]">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white dark:bg-slate-900 z-10 py-1.5 px-1 text-left font-medium text-ink-soft w-24" />
              {sortedNodes.map(n => (
                <th key={n.id} className="py-1.5 px-0.5 text-center font-medium text-ink-soft max-w-[40px]">
                  <div className="writing-mode-vertical transform -rotate-45 origin-bottom-left whitespace-nowrap ml-4 mb-1">
                    {n.name.length > 12 ? n.name.slice(0, 11) + '…' : n.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedNodes.map(row => (
              <tr key={row.id}>
                <td className="sticky left-0 bg-white dark:bg-slate-900 z-10 py-0.5 px-1 text-ink-dark font-medium truncate max-w-[96px]" title={row.name}>
                  {row.name.length > 14 ? row.name.slice(0, 13) + '…' : row.name}
                </td>
                {sortedNodes.map(col => {
                  if (row.id === col.id) {
                    return (
                      <td key={col.id} className="p-0.5">
                        <div className="w-7 h-7 rounded-sm bg-slate-100 mx-auto flex items-center justify-center text-[8px] text-ink-hint">—</div>
                      </td>
                    )
                  }
                  const pair = matrix.get(`${row.id}-${col.id}`)
                  if (!pair) {
                    return (
                      <td key={col.id} className="p-0.5">
                        <div className="w-7 h-7 rounded-sm bg-slate-50 mx-auto" />
                      </td>
                    )
                  }
                  const intensity = 1 - (pair.distance_m / maxDist)
                  return (
                    <td key={col.id} className="p-0.5">
                      <div
                        className="w-7 h-7 rounded-sm mx-auto cursor-default"
                        style={{ backgroundColor: `rgba(0, 112, 171, ${0.08 + intensity * 0.82})` }}
                        title={`${row.name} → ${col.name}: ${formatDist(pair.distance_m)}, ${formatTime(pair.walk_sec)} walk, ${pair.observations} observations`}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
